import {
  HourglassEmpty as IdleIcon,
  FiberManualRecord as LiveIcon,
  SignalWifiOff as OfflineIcon,
  PauseCircleOutline as PausedIcon,
} from "@mui/icons-material";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import type { ArgusInstanceStatus } from "../../types/argus";

export interface ArgusConnectionChipProps {
  isConnected: boolean;
  connectionError: string | null;
  instanceStatus: ArgusInstanceStatus | null;
  /**
   * Whether a session is currently active. The SSE connection only opens once
   * a session exists, so without one we're intentionally idle — not
   * "connecting" (which would imply failure if it lingered).
   */
  hasActiveSession: boolean;
}

/**
 * Compact live-status indicator for the `/argus` header. Modelled on the
 * generate-fast StatusChip pattern but collapsed to a single icon that
 * communicates connection state at a glance, with a tooltip for the details.
 *
 * States:
 *   - live       — SSE connected, icon pulses green
 *   - connecting — active session + instance but SSE still handshaking, spinner
 *   - offline    — instance active but SSE errored, red icon
 *   - paused     — instance paused/inactive, grey pause icon
 *   - idle       — instance active but no session yet (waiting for first msg)
 *   - no-inst    — no instance yet or booting
 */
export const ArgusConnectionChip = ({
  isConnected,
  connectionError,
  instanceStatus,
  hasActiveSession,
}: ArgusConnectionChipProps) => {
  let tooltip: string;
  let icon: React.ReactNode;

  if (instanceStatus === "paused") {
    tooltip = "Instance paused — resume to reconnect";
    icon = <PausedIcon sx={{ fontSize: 18, color: "text.disabled" }} />;
  } else if (instanceStatus !== "active") {
    tooltip = "No active instance";
    icon = <OfflineIcon sx={{ fontSize: 18, color: "text.disabled" }} />;
  } else if (!hasActiveSession) {
    tooltip = "Idle — send a message to start a session";
    icon = <IdleIcon sx={{ fontSize: 16, color: "text.disabled" }} />;
  } else if (isConnected) {
    tooltip = "Live — streaming events from the sandbox";
    icon = (
      <Box
        sx={{
          display: "inline-flex",
          position: "relative",
          width: 12,
          height: 12,
          alignItems: "center",
          justifyContent: "center",
          "@keyframes argus-pulse": {
            "0%": { transform: "scale(1)", opacity: 0.9 },
            "70%": { transform: "scale(2.2)", opacity: 0 },
            "100%": { transform: "scale(2.2)", opacity: 0 },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: "success.main",
            animation: "argus-pulse 1.6s ease-out infinite",
          },
        }}
      >
        <LiveIcon sx={{ fontSize: 12, color: "success.main", zIndex: 1 }} />
      </Box>
    );
  } else if (connectionError) {
    // Instance is still DB-`active` but the gateway is unreachable —
    // almost always E2B's idle auto-pause beating the health-check cron.
    // Prompt the user toward the Reconnect button in the header.
    tooltip = "Disconnected — sandbox may be paused. Click Reconnect to heal.";
    icon = <OfflineIcon sx={{ fontSize: 18, color: "error.main" }} />;
  } else {
    tooltip = "Connecting to the sandbox gateway…";
    icon = <CircularProgress size={14} thickness={5} />;
  }

  return (
    <Tooltip title={tooltip} arrow placement="bottom">
      <Box
        role="status"
        aria-label={tooltip}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 28,
          height: 28,
          borderRadius: "50%",
          cursor: "default",
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
};
